import {inject, Injectable} from '@angular/core';
import {DocumentAssignments} from '../../models/document/document-assignments.model';
import {CacheStore} from '../../stores/cache.store';
import {CacheQuery} from '../../queries/cache.query';
import {CacheItem} from '../../models/cache-item.model';
import {CacheNameEnum} from '../../enums/cache/cache-name.enum';
import {CacheGroupIdEnum} from '../../enums/cache/cache-group-id.enum';
import {AssignmentCollections} from '../../api/models/assignment-collections';

@Injectable({
    providedIn: 'root'
})
export class CacheService {
    private images: Record<string, string> = {};
    private documentAssignments: DocumentAssignments = {};
    private readonly cacheStore = inject(CacheStore);
    private readonly cacheQuery = inject(CacheQuery);

    public hasImageCached(src: string): boolean {
        return src in this.images;
    }

    public addImage(src: string, base64: string): void {
        this.images[src] = base64;
    }

    public getImageFromCache(src: string): string | undefined {
        if (this.hasImageCached(src)) {
            return this.images[src];
        }
        return undefined;
    }

    public clearImageCache(): void {
        this.images = {};
    }

    public hasDocumentAssignment(id: string): boolean {
        return id in this.documentAssignments;
    }

    public getToDocumentAssignmentCache(id: string): unknown {
        return this.documentAssignments[id];
    }

    public removeFromAssignmentCache(src: string): void {
        delete this.documentAssignments[src];
    }

    public addToDocumentAssignmentCache(id: string, data: AssignmentCollections | undefined): void {
        this.documentAssignments[id] = data;
    }

    public clearDocumentAssignmentCache(): void {
        this.documentAssignments = {};
    }

    public updateCacheItem(cacheItem: CacheItem): void {
        this.cacheStore.update({
            items: this.addCacheItemToCollection(this.getCacheItemCollection(this.cacheQuery.getCacheItems())
                .filter(item => {
                    return item.id !== cacheItem.id;
                }), cacheItem)
        });
    }

    public cacheItemHasExpired(cacheItem: CacheItem): boolean {
        return this.getTimeInSeconds(Date.now() - cacheItem.lastModified) > cacheItem.maxAgeInSeconds;
    }

    public setMaxAgeInSecondsByCacheName(name: CacheNameEnum, maxAgeInSeconds: number): void {
        const newMaxAgesInSeconds = Object.assign({}, this.cacheQuery.getValue().maxAgesInSeconds);
        newMaxAgesInSeconds[name] = maxAgeInSeconds;

        this.cacheStore.update({
            maxAgesInSeconds: newMaxAgesInSeconds
        });
    }

    public deleteCacheItemsByGroupById(id: CacheGroupIdEnum): void {
        this.cacheStore.update({
            items: this.getCacheItemCollection(this.cacheQuery.getCacheItems())
                .filter(item => {
                    return item.groupId !== id;
                })
        });
    }

    private getTimeInSeconds(timeInMs: number): number {
        return Math.round(timeInMs / 1000);
    }

    private addCacheItemToCollection(cacheItems: Array<CacheItem>, cacheItem: CacheItem): Array<CacheItem> {
        return this.getCacheItemCollection(cacheItems)
            .concat([cacheItem]);
    }

    private getCacheItemCollection(cacheItems: Array<CacheItem>): Array<CacheItem> {
        return JSON.parse(JSON.stringify(cacheItems));
    }
}
